import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Search, Eye, X, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { buildErrorToast } from '@/lib/apiError';
import {
  getAllowedTemplatesWithUsage,
  updateAllowedEmailTemplateIds,
  type IAllowedTemplateEntry,
} from '@/api/daily-report.api';

const SAMPLE_MERGE_TAGS: Record<string, string> = {
  '{{ClientName}}': 'Sample Client Co.',
  '{{ReportDate}}': new Date().toISOString().split('T')[0],
  '{{StaffCount}}': '24',
  '{{LeaveCount}}': '3',
  '{{AbsentCount}}': '1',
};

function applyMergeTags(template: string): string {
  let result = template;
  for (const [tag, value] of Object.entries(SAMPLE_MERGE_TAGS)) {
    result = result.split(tag).join(value);
  }
  return result;
}

export function EmailTemplateWhitelistSection() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<IAllowedTemplateEntry | null>(null);
  const [confirmUncheck, setConfirmUncheck] = useState<IAllowedTemplateEntry | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['daily-report-allowed-templates'],
    queryFn: getAllowedTemplatesWithUsage,
  });

  const saveMutation = useMutation({
    mutationFn: updateAllowedEmailTemplateIds,
    onSuccess: () => {
      toast({ title: 'Whitelist updated' });
      queryClient.invalidateQueries({ queryKey: ['daily-report-allowed-templates'] });
      queryClient.invalidateQueries({ queryKey: ['daily-report-config'] });
    },
    onError: (err) => buildErrorToast(toast)(err, 'Failed to update whitelist'),
  });

  const templates = data?.templates ?? [];
  const stale = data?.stale ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) => t.code.toLowerCase().includes(q) || t.name.toLowerCase().includes(q),
    );
  }, [templates, search]);

  function currentAllowedIds(): string[] {
    return templates.filter((t) => t.allowed).map((t) => t.id);
  }

  function handleCheck(template: IAllowedTemplateEntry, checked: boolean) {
    if (!checked && template.usageCount > 0) {
      setConfirmUncheck(template);
      return;
    }
    const ids = currentAllowedIds();
    const next = checked ? [...ids, template.id] : ids.filter((id) => id !== template.id);
    saveMutation.mutate(next);
  }

  function confirmRemove() {
    if (!confirmUncheck) return;
    const next = currentAllowedIds().filter((id) => id !== confirmUncheck.id);
    saveMutation.mutate(next);
    setConfirmUncheck(null);
  }

  function handleBulkClean() {
    const staleIds = new Set(stale.map((s) => s.id));
    const next = currentAllowedIds().filter((id) => !staleIds.has(id));
    saveMutation.mutate(next);
  }

  if (isLoading) {
    return <p className="text-muted-foreground py-2">Loading whitelist...</p>;
  }

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Email Template Whitelist
        </h3>
        <p className="text-xs text-muted-foreground">
          {currentAllowedIds().length} template{currentAllowedIds().length !== 1 ? 's' : ''} allowed
        </p>
      </div>

      <p className="text-sm text-muted-foreground">
        Only templates checked here will appear in the dropdown in S49 (per-client configuration).
        An empty whitelist means HR cannot enable Daily Report for any client.
      </p>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          className="pl-8 h-8 text-sm"
          placeholder="Search by Code or Name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Template table */}
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="border-b text-left text-muted-foreground text-xs">
              <th className="px-3 py-2 w-10">Allowed?</th>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Subject Preview</th>
              <th className="px-3 py-2 w-32">Used by</th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground italic">
                  {search ? 'No matching templates found.' : 'No email templates yet.'}
                </td>
              </tr>
            )}
            {filtered.map((t) => (
              <tr key={t.id} className="border-b last:border-0 hover:bg-accent/20">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={t.allowed}
                    disabled={saveMutation.isPending}
                    onChange={(e) => handleCheck(t, e.target.checked)}
                    className="rounded border-gray-300 cursor-pointer"
                  />
                </td>
                <td className="px-3 py-2 font-mono text-xs">{t.code}</td>
                <td className="px-3 py-2 font-medium">{t.name}</td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-xs">
                  {applyMergeTags(t.subject)}
                </td>
                <td className="px-3 py-2">
                  {t.usageCount > 0 ? (
                    <Badge variant="secondary" className="text-xs">
                      {t.usageCount} client{t.usageCount !== 1 ? 's' : ''}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={() => setPreviewTemplate(t)}
                    title="Preview template"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Stale entries */}
      {stale.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 flex items-center justify-between gap-3">
          <p className="text-sm text-amber-700 dark:text-amber-400">
            <strong>{stale.length} template{stale.length !== 1 ? 's' : ''}</strong> no longer exist or are disabled but still in the whitelist — remove?
          </p>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 gap-1.5 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
            onClick={handleBulkClean}
            disabled={saveMutation.isPending}
          >
            <Trash2 className="h-3 w-3" />
            Bulk clean
          </Button>
        </div>
      )}

      {/* Confirm uncheck modal */}
      <Dialog open={!!confirmUncheck} onOpenChange={(open) => { if (!open) setConfirmUncheck(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove template from whitelist?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  <strong>{confirmUncheck?.usageCount} client{(confirmUncheck?.usageCount ?? 0) !== 1 ? 's' : ''}</strong> are currently using this template.
                  After removal, the next cron run for these clients will fail (E406) until HR selects a new template in S49.
                </p>
                {confirmUncheck && confirmUncheck.usingClients.length > 0 && (
                  <ul className="list-disc pl-4 text-muted-foreground space-y-0.5">
                    {confirmUncheck.usingClients.map((c) => (
                      <li key={c.id}>{c.name}</li>
                    ))}
                  </ul>
                )}
                <p>Continue?</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmUncheck(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemove}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Confirm Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview modal */}
      {previewTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
              <div>
                <h2 className="font-semibold text-sm">{previewTemplate.name}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Subject: {applyMergeTags(previewTemplate.subject)}
                </p>
              </div>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="p-1 rounded hover:bg-accent transition-colors"
                aria-label="Close preview"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                srcDoc={applyMergeTags(previewTemplate.body)}
                title="Email preview"
                className="w-full h-full border-0"
                style={{ minHeight: 500 }}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
